import {
  useLocation as useTanstackLocation,
  useMatch as useTanstackMatch,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useCallback, useContext, useMemo } from 'react';

import { OutletContext } from './outlet-context';
import { createPath, matchPath, parsePath, resolvePath } from './path-utils';
import { RouteContext } from './route-context';
import { createSearchParams } from './search-params';

import type { AnyRouter, HistoryState, ParsedLocation } from '@tanstack/react-router';
import type { Path, PathMatch, PathPattern, To } from './path-utils';
import type { URLSearchParamsInit } from './search-params';

type Location<State = unknown> = {
  pathname: string;
  search: string;
  hash: string;
  state: State;
  key: string;
};

/** React-Router-shaped `useLocation`. `search` is the raw string (with `?`), `hash` includes `#`. */
export function useLocation<State = unknown>(): Location<State> {
  const location = useTanstackLocation();
  // TanStack types router state as its own `ParsedHistoryState`; the two router-key
  // lookups below are the only thing read from it.
  // oxlint-disable-next-line anti-slop/no-chained-type-assertions -- TanStack types state as ParsedHistoryState; only router keys are read
  const stateRecord = (location.state ?? {}) as unknown as Record<string, unknown>;

  const { pathname, search, hash } = toPath(location);
  const state = location.state as State;
  const key = (stateRecord.key as string) ?? (stateRecord.__TSR_key as string) ?? 'default';

  // React Router hands back a referentially stable `location` that only changes
  // on an actual navigation. Consumers rely on that: `useCallback`/`useMemo`/
  // effects keyed on `location` (e.g. ResultsSidebar + useFadeOutContent) loop
  // forever — React error #185 — if a fresh object is returned every render.
  return useMemo(() => ({ pathname, search, hash, state, key }), [pathname, search, hash, state, key]);
}

/**
 * Reads part of the location. The caller re-renders only when the selected value changes,
 * so return a primitive or a stable reference.
 */
export function useLocationSelector<T>(select: (location: Path) => T): T {
  // TanStack's selector types resolve only for a concrete selected type, not a generic `T`.
  return useTanstackLocation<AnyRouter, T>({ select: location => select(toPath(location)) }) as T;
}

function toPath(location: ParsedLocation): Path {
  return {
    pathname: location.pathname,
    search: location.searchStr ?? '',
    hash: location.hash ? `#${location.hash}` : '',
  };
}

/**
 * The pathname relative navigation resolves against. React Router resolves a
 * relative `to` against the *route* that rendered the link/hook (its route
 * context), not the full browser URL — so a relative `<Link to="recruit">` in a
 * component mounted at `/studies/:id` resolves to `/studies/x/recruit` even
 * when the URL is the deeper `/studies/x/build`. We mirror that by resolving
 * against the nearest route match's pathname, falling back to the full URL when
 * rendered outside any matched route (e.g. the test context wrapper).
 */
function useRouteBasePathname(): string {
  const location = useTanstackLocation();
  const matchPathname = useTanstackMatch({ strict: false, shouldThrow: false, select: match => match?.pathname });
  return matchPathname ?? location.pathname;
}

/**
 * Returns a resolver matching React Router's `resolveTo`: a `to` that carries a
 * pathname resolves against the route context (so a relative `recruit/settings`
 * is relative to the rendering route), but a `to` with only a search/hash —
 * e.g. `navigate('?block_id=welcome')` — keeps the *current* location pathname
 * rather than collapsing to the route base. Conflating the two made
 * search-only navigations jump up to the route root.
 */
export function useResolveTo(): (to: To) => Path {
  const routeBasePathname = useRouteBasePathname();
  const location = useTanstackLocation();

  return useCallback(
    (to: To) => resolveTo(to, routeBasePathname, location.pathname),
    [routeBasePathname, location.pathname],
  );
}

function resolveTo(to: To, routeBasePathname: string, locationPathname: string): Path {
  const toPathname = typeof to === 'string' ? parsePath(to).pathname : to.pathname;
  return resolvePath(to, toPathname == null ? locationPathname : routeBasePathname);
}

export type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
  preventScrollReset?: boolean;
};

export type NavigateFunction = (to: To, options?: NavigateOptions) => void;

/**
 * React-Router-shaped `useNavigate`. Resolves the (possibly relative) `to`
 * against the route context and drives the history directly, which preserves
 * React Router's raw-string URL semantics (no search re-encoding).
 */
export function useNavigate(): NavigateFunction {
  const router = useRouter();
  // Not TanStack's match context: its value changes with path params and would re-render
  // the caller. Outside a shim route (root layout, file routes) this resolves against the URL.
  const routes = useContext(RouteContext);

  // Internal param is widened to accept React Router's numeric delta form.
  // The public `NavigateFunction` type still rejects it (numeric history
  // navigation has no place in the typed surface), so the two call sites that
  // use it keep their `@ts-expect-error TANSTACK_MIGRATION` markers — but the
  // behavior (history back / reload) is preserved at runtime.
  const navigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        if (to === 0) {
          window.location.reload();
        } else {
          router.history.go(to);
        }
        return;
      }

      // Resolve the (possibly relative) `to` against the route context into a
      // single absolute href, then hand it to TanStack's `navigate`. Going
      // through `navigate` (rather than driving `history` directly) is what
      // reliably commits and re-matches the location. `resetScroll: false`
      // keeps React Router's BrowserRouter behavior — no scroll-to-top on
      // navigation (this app mounts no `<ScrollRestoration>`), which also
      // honors `preventScrollReset`.
      const { location } = router.state;
      // Typed against the registered route tree, which cannot see the grafted shim routes.
      const matches = router.state.matches as Array<{ routeId: string; pathname: string }>;
      const routeMatch = routes && matches.find(match => routes.some(route => route.id === match.routeId));
      const routeBasePathname = routeMatch?.pathname ?? location.pathname;
      const href = createPath(resolveTo(to, routeBasePathname, location.pathname));

      void router.navigate({
        href,
        replace: options?.replace,
        state: options?.state as HistoryState | undefined,
        resetScroll: false,
      });
    },
    [router, routes],
  );

  return navigate as NavigateFunction;
}

/**
 * React-Router-shaped `useParams`. Reads the merged params off the deepest
 * match via router state (rather than TanStack's `useParams`, which throws when
 * rendered outside a matched route — e.g. components mounted directly through
 * the test context wrapper). Exposes the splat under `*` (TanStack uses `_splat`).
 */
export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  // `state.matches` is typed against the *registered* (file-based) route tree,
  // which the runtime-grafted shim routes are deliberately invisible to — read
  // the raw match params through a cast instead.
  const params = useRouterState({
    select: state => {
      const matches = state.matches as Array<{ params: Record<string, string | undefined> }>;
      return matches[matches.length - 1]?.params ?? {};
    },
  });

  // Keep the returned object referentially stable (React Router's contract) so
  // effects/memos keyed on `useParams()` don't re-run every render. The splat
  // branch would otherwise spread into a fresh object on each call.
  return useMemo(() => {
    // `T` is the caller's declared param shape. TanStack infers its own from the route
    // tree, and nothing in the type system relates a route's params to an arbitrary `T`.
    if (params._splat !== undefined && params['*'] === undefined) {
      // oxlint-disable-next-line anti-slop/no-chained-type-assertions -- TanStack infers params from the route tree; T is the caller's shape
      return { ...params, '*': params._splat } as unknown as T;
    }
    // oxlint-disable-next-line anti-slop/no-chained-type-assertions -- TanStack infers params from the route tree; T is the caller's shape
    return params as unknown as T;
  }, [params]);
}

type SetURLSearchParams = (
  nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOpts?: NavigateOptions,
) => void;

/** React-Router-shaped `useSearchParams`: `[URLSearchParams, setter]` over the raw search string. */
export function useSearchParams(defaultInit?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] {
  const search = useLocationSelector(location => location.search);
  const navigate = useNavigate();
  const router = useRouter();

  // Memoise on the raw search string, like React Router — a new `URLSearchParams`
  // every render destabilises every effect/callback keyed on `searchParams`.
  const searchParams = useMemo(() => createSearchParams(search || defaultInit), [search, defaultInit]);

  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, navigateOpts) => {
      const loc = router.state.location;
      const current = createSearchParams(loc.searchStr || '');
      const newParams = createSearchParams(typeof nextInit === 'function' ? nextInit(current) : nextInit);
      const search = newParams.toString();
      navigate(
        { pathname: loc.pathname, search: search ? `?${search}` : '', hash: loc.hash ? `#${loc.hash}` : '' },
        navigateOpts,
      );
    },
    [navigate, router],
  );

  return [searchParams, setSearchParams];
}

/**
 * React-Router-shaped `useResolvedPath`: resolve a `to` against the route that
 * rendered it (its route context), mirroring `useNavigate` / `Link` and React
 * Router's route-relative default — not the full browser URL.
 */
export function useResolvedPath(to: To): Path {
  const resolve = useResolveTo();
  // Memoise like React Router so the returned `Path` is referentially stable
  // while `to` / the route context are unchanged.
  const toKey = typeof to === 'string' ? to : JSON.stringify(to);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toKey captures `to`'s content
  return useMemo(() => resolve(to), [resolve, toKey]);
}

/**
 * The pathname of the deepest route on screen. During a navigation it is still the previous route, while
 * `useLocation` already has the new URL, so a redirect decided on it never shows a stale child route.
 */
export function useRenderedPathname(): string {
  return useRouterState({ select: state => state.matches.at(-1)?.pathname ?? state.location.pathname });
}

/** React-Router-shaped `useMatch`: match a path pattern against the current pathname. */
export function useMatch<ParamKey extends string = string>(pattern: PathPattern): PathMatch<ParamKey> | null {
  const { pathname } = useLocation();
  // Memoise so the returned match object is stable while the pattern/pathname
  // are unchanged — `matchPath` builds a fresh object on every call.
  const patternKey = typeof pattern === 'string' ? pattern : JSON.stringify(pattern);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- patternKey captures `pattern`'s content
  return useMemo(() => matchPath<ParamKey>(pattern, pathname), [patternKey, pathname]);
}

/** React-Router-shaped `useOutletContext`, fed by the shim `Outlet`'s `context` prop. */
export function useOutletContext<Context = unknown>(): Context {
  return useContext(OutletContext) as Context;
}
